/**
 * School Lists API Service
 * Handles athlete school list management (dream, target, safety categories)
 */

import { BACKEND_CONFIG } from '../config/backendConfig';
import { MatchedSchool } from './schoolsAPI';

// Types for school list data
export type SchoolCategory = 'dream' | 'target' | 'safety';

export interface SchoolInList extends MatchedSchool {
  added_date: string;
  category: SchoolCategory;
}

export interface SchoolListsResponse {
  success: boolean;
  data: {
    dreamSchools: SchoolInList[];
    targetSchools: SchoolInList[];
    safetySchools: SchoolInList[];
    totals: {
      dreamCount: number;
      targetCount: number;
      safetyCount: number;
      totalCount: number;
    };
  };
}

export interface SchoolListsSummaryResponse {
  success: boolean;
  data: {
    totals: {
      dreamCount: number;
      targetCount: number;
      safetyCount: number;
      totalCount: number;
    };
    lastUpdated: string | null;
    recentlyAdded: Array<{
      school_id: number;
      name: string;
      category: SchoolCategory;
      added_date: string;
    }>;
  };
}

export interface AddSchoolResponse {
  success: boolean;
  message: string;
  data: {
    school_id: number;
    category: SchoolCategory;
    added_date: string;
    previous_category: SchoolCategory | null;
  };
}

export interface RemoveSchoolResponse {
  success: boolean;
  message: string;
  data: {
    school_id: number;
    removed_from_category: SchoolCategory;
    removed_date: string;
  };
}

export interface MoveSchoolResponse {
  success: boolean;
  message: string;
  data: {
    school_id: number;
    old_category: SchoolCategory;
    new_category: SchoolCategory;
    updated_date: string;
  };
}

// API utility function
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const url = `${BACKEND_CONFIG.baseURL}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

    
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || error.error || `HTTP ${response.status}: ${error.message || 'Unknown error'}`);
  }

  const responseData = await response.json();
    return responseData;
}

// School Lists API
export const schoolListsAPI = {
  // Get all school lists for an athlete
  async getSchoolLists(
    token: string,
    athleteId: string
  ): Promise<SchoolListsResponse> {
    return apiCall(`/athletes/${athleteId}/school-lists`, { method: 'GET' }, token);
  },

  // Get school lists summary (counts and recent additions)
  async getSchoolListsSummary(
    token: string,
    athleteId: string
  ): Promise<SchoolListsSummaryResponse> {
    return apiCall(`/athletes/${athleteId}/school-lists/summary`, { method: 'GET' }, token);
  },

  // Add school to a category (or move if already in another category)
  async addSchool(
    token: string,
    athleteId: string,
    schoolId: number,
    category: SchoolCategory
  ): Promise<AddSchoolResponse> {
    return apiCall(`/athletes/${athleteId}/school-lists`, {
      method: 'POST',
      body: JSON.stringify({
        school_id: schoolId,
        category: category
      }),
    }, token);
  },

  // Remove school from all categories
  async removeSchool(
    token: string,
    athleteId: string,
    schoolId: number
  ): Promise<RemoveSchoolResponse> {
    return apiCall(`/athletes/${athleteId}/school-lists/${schoolId}`, { method: 'DELETE' }, token);
  },

  // Move school to a different category
  async moveSchool(
    token: string,
    athleteId: string,
    schoolId: number,
    category: SchoolCategory
  ): Promise<MoveSchoolResponse> {
    return apiCall(`/athletes/${athleteId}/school-lists/${schoolId}`, {
      method: 'PUT',
      body: JSON.stringify({
        category: category
      }),
    }, token);
  },
};
